/**
 * Item — collectible power-up pickup (Brief 010).
 *
 * ONE entity class, `props.kind` selects the effect (mirrors FLYER/WALKER variant):
 *   'heart'          — heal +amount hearts (clamped to maxHearts by World). Sprite item_heart.
 *   'heartContainer' — raise maxHearts +1 (capped) and fill it. Sprite item_heart_container.
 *   'star'           — grant a timed invuln of props.durationMs. Sprite item_star.
 *   'dashRefill'     — DASH-PRECISION: restore the air-dash charge (+ climb stamina) mid-air —
 *                      the Celeste "green gem". Routes via ctx.player.refillDash(); no buff hook.
 *
 * Behaviour mirrors COIN: floats at a fixed position (no gravity), bobs ±4 px on a
 * deterministic triangle wave, and on player overlap routes to the matching
 * EntityContext BUFF hook → plays a pickup SFX → despawns. The entity only REQUESTS
 * the buff; World owns the policy/edge-rules (heal clamp, capacity cap, invuln refresh).
 *
 * Locked: a power-up is NEVER a hidden health layer — it touches the hearts meter or a
 * timed buff only (Zelda model). 'star' reuses the i-frame window, just longer.
 *
 * Determinism: Math.abs / Math.floor / arithmetic only — ESLint-clean under src/engine/.
 * NO Math.sin (triangle wave, copied from COIN); invuln steps derive from msToSteps
 * (integer), so the same seed bobs + buffs identically on replay.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';
import { msToSteps } from '../buffs';

/** Pixels the item bobs up and down from its spawn Y (matches COIN). */
const BOB_AMPLITUDE = 4;
/** Full bob cycle in seconds. */
const BOB_PERIOD = 1.2;

/** Power-up kinds. Additive — a future 'form' token (P3) appends here. */
export type ItemKind = 'heart' | 'heartContainer' | 'star' | 'dashRefill';

/** Default hearts healed by a 'heart' pickup. */
const DEFAULT_HEAL_AMOUNT = 1;
/** Default 'star' invuln duration (ms) when props.durationMs is absent. */
const DEFAULT_STAR_DURATION_MS = 8000;

/** Logical sprite key per kind (mapped in AssetManifest; fall back to overworld art). */
const SPRITE_BY_KIND: Record<ItemKind, string> = {
  heart: 'item_heart',
  heartContainer: 'item_heart_container',
  star: 'item_star',
  dashRefill: 'item_star', // reuse the star art for v1 (distinct dash-gem art is OWED)
};

function coerceKind(raw: unknown): ItemKind {
  return raw === 'heart' || raw === 'heartContainer' || raw === 'star' || raw === 'dashRefill' ? raw : 'heart';
}

export class Item implements Entity {
  readonly type = EntityType.ITEM;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private readonly kind: ItemKind;
  private readonly amount: number;
  private readonly durationSteps: number;
  private spawnY: number;
  private bobPhase = 0; // 0..BOB_PERIOD — drives the triangle wave
  private collected = false;

  /**
   * @param cx     spawn center-x (world px)
   * @param feetY  bottom of the entity's tile cell — the item floats above the floor
   * @param props  kind ('heart'|'heartContainer'|'star'), amount (heal), durationMs (star)
   */
  constructor(cx: number, feetY: number, props?: Record<string, unknown>) {
    this.kind = coerceKind(props?.kind);
    const a = props?.amount;
    this.amount = typeof a === 'number' && a > 0 ? Math.floor(a) : DEFAULT_HEAL_AMOUNT;
    const d = props?.durationMs;
    this.durationSteps = msToSteps(typeof d === 'number' && d > 0 ? d : DEFAULT_STAR_DURATION_MS);
    // 40×40 hitbox floating in the cell — same footprint/offset as COIN.
    this.spawnY = feetY - 52;
    this.body = { x: cx - 20, y: this.spawnY, w: 40, h: 40 };
  }

  update(dt: number, _ctx: EntityContext): void {
    if (this.collected) return;
    // Triangle-wave bob (no Math.sin): phase → [0,1) → 1-|2t-1| → [0..1..0] → ±amp.
    this.bobPhase = (this.bobPhase + dt) % BOB_PERIOD;
    const t = this.bobPhase / BOB_PERIOD;
    const triangle = 1 - Math.abs(2 * t - 1);
    this.body.y = this.spawnY + BOB_AMPLITUDE * (triangle * 2 - 1);
  }

  onPlayerOverlap(ctx: EntityContext): void {
    if (this.collected) return;
    switch (this.kind) {
      case 'heart':
        ctx.healPlayer(this.amount);
        ctx.playSound('coin', 0.55);
        break;
      case 'heartContainer':
        ctx.addMaxHeart();
        ctx.playSound('select', 0.6);
        break;
      case 'star':
        ctx.grantInvuln(this.durationSteps);
        ctx.playSound('coin', 0.7);
        break;
      case 'dashRefill':
        // DASH-PRECISION: restore the air-dash charge (+ climb stamina) mid-air. Asks the
        // Player directly via ctx.player — no World buff hook needed (it's not a hearts/invuln
        // change). No-op on a non-DASH-PRECISION level (the charge model is inactive).
        ctx.player.refillDash();
        ctx.playSound('coin', 0.6);
        break;
    }
    const cx = this.body.x + this.body.w / 2, cy = this.body.y + this.body.h / 2;
    ctx.emitFx?.('sparkle', cx, cy, { color: this.kind === 'star' ? 0xfff27a : 0xff8ab0 });
    this.collected = true;
    this.alive = false;
  }

  spriteKey(): string { return SPRITE_BY_KIND[this.kind]; }

  visible(): boolean { return !this.collected; }
}
